// A library asset's own moderation state, shared by the media library and
// the composer's library picker. Nothing here fetches or renders.
import { checkFailureClass } from './moderationWords'

// What a failed check of library media needs, by checkFailureClass: only
// an outage is tried again on its own; a refused result waits on a Wager
// fix of the provider.
const CHECK_FAILED_DESCRIPTIONS = {
  outage: 'The moderation check failed. It is tried again automatically, within an hour or sooner once the provider works again. Posts that use this media stay held until it passes.',
  refused: "The moderation provider answered with a result Wager's rules refuse (a rejected or blocking soundtrack result). That is a fault in the provider and it is not retried: Wager has to fix the provider, then replace this media in the posts that use it to check it again. Wager can release those posts meanwhile.",
  media: 'Moderation could not read this media. Replace it in the posts that use it; they stay held until then.',
  content: 'The moderation provider refused to check this media. Replace it in the posts that use it; they stay held until then.',
  scope: "Moderation is not set up for one of the regulations of the posts that use this media, so it could not be checked. Change those posts' regulations, or ask Wager to set them up; it is checked again once they resolve.",
}

// assetModerationState reads the list read model's moderation field. Only
// states an operator can act on or should know about render. Quarantined,
// rejected and flagged are real verdicts and always show. A pending or
// failed check holds posts only while moderation is enforced for this
// library, so "Checking" and "Check failed" render only then. Pending alone
// is not "Checking": an org-less or switched-off video has no job until a
// post renders it, so "Checking" also needs a queued or running check. A
// failed check is retried automatically only when it failed on the provider
// (failure_code of class outage, see checkFailureClass); a result the
// provider should never have given (class refused) waits on a Wager fix.
// Media moderation cannot read, or that the provider refused to check, needs
// replacing; an unresolved regulation needs the regulations fixed.
export function assetModerationState(asset) {
  const moderation = asset?.moderation
  if (!moderation) return null
  const enforced = moderation.enforced === true
  if (moderation.quarantined) {
    return {
      key: 'quarantined',
      label: 'Quarantined',
      tone: 'danger',
      description: 'Moderation removed these bytes from public storage and purged the CDN edge. Devices that already downloaded them may keep a cached copy.',
    }
  }
  if (moderation.check_failed && enforced) {
    return {
      key: 'check_failed',
      label: 'Check failed',
      tone: 'warning',
      description: CHECK_FAILED_DESCRIPTIONS[checkFailureClass(moderation.failure_code)] || CHECK_FAILED_DESCRIPTIONS.outage,
    }
  }
  switch (moderation.status) {
    case 'rejected':
      return { key: 'rejected', label: 'Rejected', tone: 'danger', description: 'Moderation rejected this media. Posts that use it cannot be published.' }
    case 'flagged':
      return { key: 'flagged', label: 'Flagged', tone: 'warning', description: 'Moderation flagged this media for review. Each post that uses it is reviewed on its own.' }
    case 'pending':
      return enforced && moderation.check_pending === true && asset.asset_type === 'video'
        ? { key: 'checking', label: 'Checking', tone: 'neutral', description: "The video's check is queued or running. Posts that use it publish once it passes." }
        : null
    default:
      return null
  }
}

// assetAttachBlocked reports whether an asset cannot go into a post: while
// moderation enforces the library, a save that renders rejected media is
// refused, so the picker keeps it out.
export function assetAttachBlocked(asset) {
  return asset?.moderation?.enforced === true && asset.moderation.status === 'rejected'
}
